import { useSyncExternalStore } from 'react';
import { resetFirstLoadSplash } from '../../hooks/ui/useFirstLoadSplash';
import { ldLog } from '../../utils/ui/loginDebug';

/**
 * Sign-in handoff cover (2026-09-26, founder: "I still see blank stuff after
 * login — make it ONE continuous flow").
 *
 * Login's own splash signal dies when Login unmounts at navigation; after that
 * the screen passed through several independent loading states (the route
 * chunk, ProtectedRoute's tenant + 2FA checks, the dashboard's first data) and
 * any commit where none of them was signalling showed a blank frame — or the
 * skeleton, because the dashboard's first-load hold only fired once per PAGE
 * load, never on a second sign-in in the same tab.
 *
 * Login raises this cover at 'leaving' (the splash is already up); it lives
 * above the session-keyed tree (SessionScopedShell) so it survives Login's
 * unmount, and the destination guard (ProtectedRoute / PlatformRoute) releases
 * it once real content has painted — by then the dashboard's first-load hold,
 * if any, has already taken over, so the splash goes away exactly once, onto
 * real data. Failsafe: it can never strand anyone.
 */
const FAILSAFE_MS = 6000;

let active = false;
let failsafe = null;
const listeners = new Set();

function set(next) {
  if (next === active) return;
  active = next;
  ldLog('signincover', { active });
  listeners.forEach((l) => l());
}

export function releaseSignInCover() {
  if (failsafe) { clearTimeout(failsafe); failsafe = null; }
  set(false);
}

export function raiseSignInCover() {
  // Every sign-in is a "first load": the dashboard holds the splash until its
  // data lands instead of flashing the skeleton on a second sign-in.
  resetFirstLoadSplash();
  if (failsafe) clearTimeout(failsafe);
  failsafe = setTimeout(releaseSignInCover, FAILSAFE_MS);
  set(true);
}

/**
 * Release once the destination has PAINTED (two frames), so anything that
 * mounted in the same commit — the dashboard's first-load hold — has already
 * registered its own splash signal. A no-op when no cover is up.
 */
export function releaseSignInCoverAfterPaint() {
  if (!active) return;
  if (typeof requestAnimationFrame !== 'function') { releaseSignInCover(); return; }
  requestAnimationFrame(() => requestAnimationFrame(releaseSignInCover));
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSignInCoverActive() {
  return useSyncExternalStore(subscribe, () => active, () => false);
}

/** @internal test helper */
export function __resetSignInCoverForTests() {
  if (failsafe) clearTimeout(failsafe);
  failsafe = null;
  active = false;
  listeners.clear();
}
